import { datesEqual } from "./dateCompare";
import { OFF_ATTRIBUTE_VALUE } from "./installerHasFullWeekOff";

// - - - - - - - - - -
// Per-cell decision for issue #138 — "Red highlight on tomorrow's empty
// cells in Optimizer."
//
// Returns true iff the (installer, dateString) day-cell should be rendered
// with the red treatment (2px solid red outline + ~10% red tint). The
// caller is responsible for applying the styling and for suppressing the
// call entirely during Time Machine mode (live-only feature).
//
// The rule (resolved 2026-05-18 — see issue #138 body for the full team
// discussion / scope clarification):
//
//   1. The date must equal the next business day from today
//      (`nextBusinessDayDateString`, produced by getNextBusinessDayDateString
//       in nextBusinessDay.js). Cells on any other date are never highlighted.
//   2. The installer must NOT have an OFF attribute on that date. Both daily
//      and default-pattern OFF show up in scheduleArray as docs with
//      `attribute === true && encodedSmallTitle === "OFF"`. (Default
//      attributes are materialized per-weekday in schedule.context.js's
//      buildScheduleArray.) Holidays are handled this way — the bulk-stamping
//      mutation in dbtools writes OFF attributes for every installer on the
//      holiday date, so the cell-level check naturally suppresses the red
//      treatment on the holiday column.
//   2b. The installer must NOT have a compliance-hold marker on that date.
//      Compliance hold docs are constructed in schedule.context.js:1617–1647
//      for any installer with `candidate.Scheduling_Hold_Effective_Date` set,
//      and rendered as the "🔥 compliance hold 🔥" tile. They carry the
//      explicit `complianceHold: true` flag. A non-compliant installer
//      CANNOT be scheduled for any job — same operational outcome as OFF —
//      so the cell is not a real opening.
//   3. The installer must NOT have any scheduled job whose date range covers
//      that cell — jobs may span multiple days (date_scheduled → date_estimatedCompletion).
//      The render layer in schedule.context.js uses `start <= cellDate <= end`
//      (~30 inline copies) to decide which cells display a multi-day job tile;
//      this helper mirrors that logic so a multi-day job booked on tomorrow's
//      column does not falsely register as empty.
//      Job docs also intentionally drop the location filter (matching the
//      production comment at schedule.context.js:1893) so shared-installer
//      cross-location assignments still count as "booked."
//
// What counts as "empty" (still red — by design):
//   - Truly blank cell (no scheduleArray docs at all)
//   - Cell with only a custom-title attribute (TRAINING, BUFFER, etc.) but
//     no real job
//   - Cell with only an Extra Cell record but no real job
// The aggressive interpretation is intentional — we want to surface gaps
// that attribute-markers might be hiding. The install manager needs the
// red cue to fill them before crews are sent home with no work.

// Local helper — mirrors the inline `new Date(cell) >= new Date(start) &&
// new Date(cell) <= new Date(end)` pattern in schedule.context.js (~30 sites).
// Returns false on any unparseable date so a malformed doc cannot produce a
// false-positive job match (which would silently suppress a legitimate red
// highlight). Empty `end` is treated as a single-day job (start == end), to
// match how newJob.end is populated in schedule.context.js when
// date_estimatedCompletion is absent.
const dateInJobRange = (cellDate, jobStart, jobEnd) => {
  if (!cellDate || !jobStart) return false;
  const cellMs = new Date(cellDate).getTime();
  const startMs = new Date(jobStart).getTime();
  if (isNaN(cellMs) || isNaN(startMs)) return false;
  const effectiveEnd = jobEnd || jobStart;
  const endMs = new Date(effectiveEnd).getTime();
  if (isNaN(endMs)) return false;
  return cellMs >= startMs && cellMs <= endMs;
};

export function shouldHighlightEmptyTomorrowCell({
  dateString,
  nextBusinessDayDateString,
  scheduleArray,
  payrollNo,
  locationFilter,
}) {
  if (!dateString || !nextBusinessDayDateString) return false;
  if (!datesEqual(dateString, nextBusinessDayDateString)) return false;
  if (!Array.isArray(scheduleArray)) return false;
  if (!payrollNo) return false;

  // Either OFF or compliance hold means the installer cannot work that day.
  // Both render as attribute docs (attribute === true). Compliance hold is
  // identified by the explicit `complianceHold: true` flag (set in
  // schedule.context.js:1642); OFF is identified by the encodedSmallTitle
  // matching the canonical value.
  const isUnavailable = scheduleArray.some(
    (doc) =>
      doc &&
      doc.attribute === true &&
      doc.installer === payrollNo &&
      doc.location === locationFilter &&
      (doc.encodedSmallTitle === OFF_ATTRIBUTE_VALUE ||
        doc.complianceHold === true) &&
      datesEqual(doc.start, dateString)
  );
  if (isUnavailable) return false;

  const hasJob = scheduleArray.some(
    (doc) =>
      doc &&
      doc.attribute !== true &&
      doc.installer === payrollNo &&
      // location filter deliberately dropped here — production render in
      // schedule.context.js does the same (line 1893 comment: "disabled this
      // filter so that shared jobs from other locations will apply here").
      dateInJobRange(dateString, doc.start, doc.end)
  );
  if (hasJob) return false;

  return true;
}
